import React, { useRef, useState } from "react"
import { useFormikContext } from "formik"
import { useRouteLoaderData } from "react-router-dom"
import { getJobByLocation } from "@app/utils/inspection"
import {
	buildInitialStandardObjects,
	processCustomerMeasures,
	processDangerMeasures,
	setupElements,
	syncValuesIn,
	updateDangerMeasures
} from "@app/components/inspection-form/StandardPsaObserver.utils.jsx"

export const useStandardPsaObserver = ((currentFieldsets) => {
	const { values, setValues } = useFormikContext()
	const { jobs, office } = useRouteLoaderData("root") || {}

	const currentJobId = values["taetigkeit_beschreibung_ueberlassungals__ID"]
	const [prevJobId, setPrevJobId] = useState(currentJobId)

	const dangerValuesArray = values.physischegefaehrdungsanalyse_gefaehrdungen_gefaehrdungen
	const prevDangerValuesArray = useRef(dangerValuesArray) // TODO Umbau ref

	const standardMeasureValues = JSON.stringify({
		standardgefaehrdungen_psa: values.standardgefaehrdungen_psa,
		standardgefaehrdungen_vorsorgen: values.standardgefaehrdungen_vorsorgen,
		standardgefaehrdungen_unterweisungen: values.standardgefaehrdungen_unterweisungen,
	})
	const standardMeasureValuesRef = useRef(standardMeasureValues)

	const job = jobs.find(({ Berufschluessel }) => Berufschluessel === currentJobId)
	const dangerMeasures = processDangerMeasures(values)

	// set up current measure fields
	const resInitial = React.useMemo(() => {
		const standardPsaFieldset = currentFieldsets.find(({ identifier }) => identifier === "standardgefaehrdungen")
		if (job && standardPsaFieldset) {
			const jobByLocation = getJobByLocation(values, office)
			const processedCustomerMeasures = processCustomerMeasures(jobByLocation?.Vorsorge_PSA)

			setupElements(
				standardPsaFieldset.elements.find(({ id }) => id === "standardgefaehrdungen_psa"),
				job?.PSA,
				processedCustomerMeasures.PSA,
				dangerMeasures.PSA
			)

			setupElements(
				standardPsaFieldset.elements.find(({ id }) => id === "standardgefaehrdungen_vorsorgen"),
				job?.Vorsorgeuntersuchung,
				processedCustomerMeasures.Vorsorge,
				dangerMeasures.Vorsorge
			)

			setupElements(
				standardPsaFieldset.elements.find(({ id }) => id === "standardgefaehrdungen_unterweisungen"),
				job?.Unterweisung,
				processedCustomerMeasures.Unterweisungen,
				dangerMeasures.Unterweisungen
			)
		}

		return {
			fieldsets: currentFieldsets
		}
	}, [currentFieldsets])

	let res = React.useMemo(() => {
		if ((!prevJobId && job) || prevJobId !== currentJobId) {
			setValues({
				...values,
				...buildInitialStandardObjects(values, office, job, dangerMeasures)
			})
			setPrevJobId(currentJobId)
		}

		return {
			fieldsets: resInitial.fieldsets,
		}
	}, [currentJobId, prevJobId, job, resInitial.fieldsets])

	// synchronize values from "Gefährdungsanalyse" to "Standardanforderungen"
	res = React.useMemo(() => {
		if ((!prevDangerValuesArray.current && dangerValuesArray) || prevDangerValuesArray.current !== dangerValuesArray) {
			const prevDangerMeasures = processDangerMeasures({
				physischegefaehrdungsanalyse_gefaehrdungen_gefaehrdungen: prevDangerValuesArray.current
			})
			const initialStandardObjects = buildInitialStandardObjects(values, office, job, dangerMeasures)

			// PSA
			values.standardgefaehrdungen_psa = syncValuesIn(
				values.standardgefaehrdungen_psa,
				Object.keys(initialStandardObjects.standardgefaehrdungen_psa),
				prevDangerMeasures.PSA,
				dangerMeasures.PSA
			)

			// Vorsorgen
			values.standardgefaehrdungen_vorsorgen = syncValuesIn(
				values.standardgefaehrdungen_vorsorgen,
				Object.keys(initialStandardObjects.standardgefaehrdungen_vorsorgen),
				prevDangerMeasures.Vorsorge,
				dangerMeasures.Vorsorge
			)

			// Unterweisungen
			values.standardgefaehrdungen_unterweisungen = syncValuesIn(
				values.standardgefaehrdungen_unterweisungen,
				Object.keys(initialStandardObjects.standardgefaehrdungen_unterweisungen),
				prevDangerMeasures.Unterweisungen,
				dangerMeasures.Unterweisungen
			)

			setValues(values)
		}
		return {
			fieldsets: res.fieldsets,
		}
	}, [dangerValuesArray, res.fieldsets])

	// synchronize values from "Standardanforderungen" to "Gefährdungsanalyse"
	res = React.useMemo(() => {
		if ((!standardMeasureValuesRef && standardMeasureValues) || standardMeasureValuesRef.current !== standardMeasureValues) {
			let newDangerValues = values.physischegefaehrdungsanalyse_gefaehrdungen_gefaehrdungen
			if (newDangerValues) {
				Object.entries(values.standardgefaehrdungen_psa).forEach(([key, value]) => {
					updateDangerMeasures(newDangerValues, key, value)
				})
				Object.entries(values.standardgefaehrdungen_unterweisungen).forEach(([key, value]) => {
					updateDangerMeasures(newDangerValues, key, value)
				})
				Object.entries(values.standardgefaehrdungen_vorsorgen).forEach(([key, value]) => {
					updateDangerMeasures(newDangerValues, key, value)
				})

				standardMeasureValuesRef.current = standardMeasureValues
				setValues(values)
			}
		}
		return {
			fieldsets: res.fieldsets,
		}
	}, [standardMeasureValues, res.fieldsets])

	return res
})

const StandardPsaObserver = (({ currentFieldsets, setCurrentFieldsets, ...rest }) => {
	const { fieldsets } = useStandardPsaObserver(currentFieldsets)
	setCurrentFieldsets(fieldsets)
	return null
})

export default StandardPsaObserver
